import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import type { EligibilityStatus, SalesProvider } from "../types";
import { SaleProgress } from "../types";

export interface EligibleProduct {
    id: string;
    productSku: string;
    status: EligibilityStatus;
    saleProgress: SaleProgress;
    isPrimaryProduct: boolean;
    confirmationCodePattern?: string;
    needsAddress?: string[] | undefined;
    isProductAsurionBilled?: boolean;
    requiresNyDisclosures?: boolean;
    isConfettiShown?: boolean;
    salesProvider: SalesProvider;
}

type ProductState = {
    products: EligibleProduct[];
    selectedProduct: EligibleProduct | undefined;
};

interface ProductOfferStore {
    productStateBySession: Record<string, ProductState>;

    getProductState: (sessionId: string) => ProductState;
    getProducts: (sessionId: string) => EligibleProduct[];
    getPrimaryProducts: (sessionId: string) => EligibleProduct[];
    getOtherProducts: (sessionId: string) => EligibleProduct[];
    getSelectedProduct: (sessionId: string) => EligibleProduct | undefined;
    getSessionIdForProductId: (productId: string) => string | undefined;

    updateProducts: (sessionId: string, product: EligibleProduct[]) => void;
    updateProduct: (
        sessionId: string,
        productId: string,
        change: { saleProgress?: SaleProgress; status?: EligibilityStatus; isConfettiShown?: boolean },
    ) => void;
    updateProductsConfettiStatus: (sessionId: string) => void;
    updateSelectedProduct: (sessionId: string, product: EligibleProduct | undefined) => void;

    isPrimaryProductInProgress: (sessionId: string) => boolean;

    resetSaleSession: (sessionId: string) => void;
}

export const useProductOfferStore = createWithEqualityFn<ProductOfferStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    productStateBySession: {},

                    updateProducts: (sessionId, products) => {
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    products: products,
                                };
                            },
                            false,
                            "updateProducts",
                        );
                    },
                    updateProduct: (sessionId, productId, change) => {
                        set(
                            (state) => {
                                const { products, selectedProduct } = state.getProductState(sessionId);
                                if (!products) {
                                    return;
                                }

                                const newPrimaryProducts = products.map((currentProduct) =>
                                    currentProduct.id === productId ? { ...currentProduct, ...change } : currentProduct,
                                );

                                const newSelectedProduct =
                                    selectedProduct?.id === productId
                                        ? { ...selectedProduct, ...change }
                                        : selectedProduct;

                                state.productStateBySession[sessionId] = {
                                    ...state.productStateBySession[sessionId],
                                    products: newPrimaryProducts,
                                    selectedProduct: newSelectedProduct,
                                };
                            },
                            false,
                            "updateProduct",
                        );
                    },
                    updateProductsConfettiStatus: (sessionId) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const updatedProducts = currentProductState.products.map((product) => {
                                    if (product.saleProgress === SaleProgress.Success && !product.isConfettiShown) {
                                        return { ...product, isConfettiShown: true };
                                    }
                                    return product;
                                });
                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProductsConfettiStatus",
                        ),
                    updateSelectedProduct: (sessionId, product) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    selectedProduct: product,
                                };
                            },
                            false,
                            "updateSelectedProduct",
                        ),
                    isPrimaryProductInProgress: (sessionId) => {
                        return (
                            get()
                                .getPrimaryProducts(sessionId)
                                ?.some((p) => p.saleProgress === SaleProgress.InProgress) ?? false
                        );
                    },
                    getProductState: (sessionId) => {
                        return (
                            get().productStateBySession[sessionId] ?? {
                                products: [],
                                selectedProduct: undefined,
                            }
                        );
                    },
                    getProducts: (sessionId) => {
                        return get().getProductState(sessionId).products;
                    },
                    getPrimaryProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => p.isPrimaryProduct) ?? [],
                    getOtherProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => !p.isPrimaryProduct) ?? [],
                    getSelectedProduct: (sessionId) => {
                        return get().getProductState(sessionId).selectedProduct;
                    },
                    getSessionIdForProductId: (productId) => {
                        return Object.keys(get().productStateBySession).find((sessionId) =>
                            get()
                                .getProductState(sessionId)
                                .products.some((product) => product.id === productId),
                        );
                    },
                    resetSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                            },
                            false,
                            "resetSaleSession",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "product-offer-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        { name: "product-offer-store", skipHydration: true },
    ),
    shallow,
);
